import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faSearchDollar } from "@fortawesome/pro-regular-svg-icons/faSearchDollar";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import Skeleton from "antd/es/skeleton";
import { get } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import Located from "@mapmycustomers/shared/types/base/Located";
import { convertCoordinatesToLongLat } from "@mapmycustomers/shared/util/geo/GeoService";
import { isEmptyString } from "@mapmycustomers/shared/util/stringUtils";
import { Button } from "@mapmycustomers/ui";

import Call from "@app/component/actions/Call";
import VisitWebsite from "@app/component/actions/VisitWebsite";
import SingleLineAddress from "@app/component/field/SingleLineAddress";
import LeadItemHours from "@app/component/LeadItem/components/LeadItemHours";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import { getLeadGenPlan, isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatCoordinates } from "@app/util/formatters";

import LeadFinderPlanAlert from "./LeadFinderPlanAlert";
import styles from "./LeadPopover.module.scss";

const messages = defineMessages({
  createCompany: {
    id: "map.mapPoint.lead.annotation.createMenu.company",
    defaultMessage: "Add Lead as Company",
    description: "Create as Company menu item title",
  },
  createPerson: {
    id: "map.mapPoint.lead.annotation.createMenu.person",
    defaultMessage: "Add Lead as Person",
    description: "Create as Person menu item title",
  },
});

interface Props {
  address?: Located;
  coordinates: GeolocationCoordinates;
  isReadOnlyMember: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  loading?: boolean;
  onCancel: () => void;
  onCreateCompany: () => void;
  onCreatePerson: () => void;
  placeResult: google.maps.places.PlaceResult;
  title: string;
}

const LeadPinPopover: React.FC<Props> = ({
  address,
  coordinates,
  isReadOnlyMember,
  leadGenPlan,
  loading,
  onCancel,
  onCreateCompany,
  onCreatePerson,
  placeResult,
  title,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  const phone: string =
    get(placeResult, "formatted_phone_number") ??
    get(placeResult, "international_phone_number") ??
    "";
  const website: string = get(placeResult, "website") ?? "";

  const hasPhone = !isEmptyString(phone);
  const hasWebsite = !isEmptyString(website);

  const handlePhoneClick = useCallback(() => {
    analytics.clicked(["Phone number"]);
  }, [analytics]);

  const handleWebsiteClick = useCallback(() => {
    analytics.clicked(["Website"]);
  }, [analytics]);

  const dropDownItems: MenuProps["items"] = [
    {
      key: "1",
      label: intl.formatMessage(messages.createPerson),
      onClick: onCreatePerson,
    },
  ];

  return (
    <div className={styles.container}>
      <Interactive>
        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.title}>{title}</div>
            <Button
              className={styles.crossIcon}
              icon={<FontAwesomeIcon icon={faXmark} />}
              onClick={onCancel}
              type="text"
            />
          </div>
          {loading ? (
            <Skeleton loading title={false} />
          ) : (
            <>
              <SingleLineAddress address={address} />
              {leadGenPlan === LeadFinderSubscriptionPlan.PRO ? (
                <>
                  <div className={styles.coordinates}>
                    {formatCoordinates(convertCoordinatesToLongLat(coordinates))}
                  </div>
                  <LeadItemHours openingHours={placeResult.opening_hours} />
                  <div className={styles.actions}>
                    <Call disabled={!hasPhone} onClick={handlePhoneClick} phoneNumber={phone} />
                    <VisitWebsite
                      disabled={!hasWebsite}
                      onClick={handleWebsiteClick}
                      url={website}
                    />
                  </div>
                  {!isReadOnlyMember && (
                    <div className={styles.footer}>
                      <Dropdown.Button
                        className={styles.dropdownBtn}
                        icon={
                          <FontAwesomeIcon className={styles.dropdownIcon} icon={faChevronDown} />
                        }
                        menu={{ items: dropDownItems }}
                        onClick={onCreateCompany}
                        overlayClassName={styles.dropdown}
                        overlayStyle={{ minWidth: "19rem" }}
                        trigger={["click"]}
                      >
                        <FontAwesomeIcon icon={faSearchDollar} />
                        {intl.formatMessage(messages.createCompany)}
                      </Dropdown.Button>
                    </div>
                  )}
                </>
              ) : (
                <LeadFinderPlanAlert />
              )}
            </>
          )}
        </div>
      </Interactive>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
  leadGenPlan: getLeadGenPlan(state),
});

export default connect(mapStateToProps)(LeadPinPopover);
